import React, { useState } from 'react';
import { Megaphone, Trophy, ChevronDown, ChevronUp, BellOff, Clock, Infinity as InfinityIcon } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Button } from '../ui/button';
import { easerPost, fmtShortDate } from '../../lib/easerApi';
import { notificationImageUrl, KIND_LABELS, notifyNotificationsChanged } from '../../lib/easerNotifications';

/**
 * GRH > Notifications: every notification kept for this Easer (agency messages & challenges).
 * Opening an unread one records its first read (once) on the server.
 */
export function EaserNotifications({ items, onUnauthorized, onRead }) {
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState('all'); // all | unread

  const open = async (n) => {
    const next = openId === n.id ? null : n.id;
    setOpenId(next);
    if (next && !n.read_at) {
      try {
        const res = await easerPost(`/api/easer/notifications/${n.id}/read`);
        onRead?.(n.id, res.data.read_at);
        notifyNotificationsChanged();
      } catch (err) {
        if (err?.response?.status === 401) onUnauthorized?.();
      }
    }
  };

  if (items === null) return <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>;

  const unread = items.filter((n) => !n.read_at).length;
  const visible = filter === 'unread' ? items.filter((n) => !n.read_at) : items;

  return (
    <div className="space-y-3" data-testid="easer-notifications">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Messages et challenges de l'agence.</p>
        <div className="flex rounded-lg border border-border bg-card p-0.5">
          {[['all', `Toutes (${items.length})`], ['unread', `Non lues (${unread})`]].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setFilter(id)} data-testid={`notifications-filter-${id}`}
              className={`px-3 h-9 rounded-md text-xs font-medium transition-colors ${filter === id ? 'bg-[hsl(var(--primary))] text-white' : 'text-muted-foreground hover:text-foreground'}`}>{label}</button>
          ))}
        </div>
      </div>

      {visible.length === 0 && (
        <Card className="p-8 text-center" data-testid="notifications-empty">
          <BellOff className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">{filter === 'unread' ? 'Tout est lu' : 'Aucune notification'}</p>
          <p className="text-sm text-muted-foreground mt-1">Les messages et challenges de l'agence apparaîtront ici.</p>
        </Card>
      )}

      {visible.map((n) => {
        const isOpen = openId === n.id;
        const challenge = n.kind === 'challenge';
        return (
          <Card key={n.id} className={`overflow-hidden border-border shadow-sm ${!n.read_at ? 'border-l-4 border-l-[hsl(var(--accent))]' : ''}`} data-testid="notification-item">
            <button type="button" onClick={() => open(n)} className="w-full text-left p-4 flex items-start gap-3 min-h-[44px]" aria-expanded={isOpen} data-testid="notification-toggle">
              <span className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${challenge ? 'bg-[hsl(var(--brand-apricot))]/15 text-[hsl(var(--brand-apricot))]' : 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'}`}>
                {challenge ? <Trophy className="h-5 w-5" /> : <Megaphone className="h-5 w-5" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wide">{KIND_LABELS[n.kind] || 'Message'}</Badge>
                  {!n.read_at && <Badge className="bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))] text-[10px]" data-testid="notification-new">Nouveau</Badge>}
                  <span className="text-xs text-muted-foreground ml-auto flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtShortDate(n.created_at)}</span>
                </span>
                <span className="block font-semibold mt-1 leading-snug" data-testid="notification-title">{n.title}</span>
                {!isOpen && n.body && <span className="block text-sm text-muted-foreground mt-0.5 line-clamp-2">{n.body}</span>}
              </span>
              {isOpen ? <ChevronUp className="h-5 w-5 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />}
            </button>
            {isOpen && (
              <div className="px-4 pb-4 space-y-3" data-testid="notification-body">
                {n.has_image && <img src={notificationImageUrl(n.id)} alt="" className="w-full rounded-xl border border-border max-h-80 object-contain bg-secondary/40" loading="lazy" />}
                {n.body && <p className="text-sm leading-relaxed whitespace-pre-wrap">{n.body}</p>}
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  {n.permanent ? <><InfinityIcon className="h-3.5 w-3.5" /> Conservée en permanence</> : <><Clock className="h-3.5 w-3.5" /> Conservée jusqu'au {fmtShortDate(n.keep_until)}</>}
                  {n.read_at ? ` · lue le ${fmtShortDate(n.read_at)}` : ''}
                </p>
                <Button variant="outline" size="sm" className="h-9" onClick={() => setOpenId(null)} data-testid="notification-close">Fermer</Button>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
